
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/ui/logo";
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading, isAuthStatusKnown } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Only redirect once we know authentication status
    if (isAuthStatusKnown) {
      if (!loading && !user) {
        navigate(`/login?from=${encodeURIComponent(location.pathname)}`);
      } else if (!loading && user) {
        // User is authenticated, allow access
        setIsCheckingAuth(false);
      }
    }
  }, [user, loading, navigate, location.pathname, isAuthStatusKnown]);

  // Show loading while checking auth or fetching user data
  if (loading || isCheckingAuth) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background">
        <div className="animate-pulse mb-8">
          <Logo variant="full" size="lg" />
        </div>
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return user ? (
    <>
      {children}
      <FeedbackWidget />
    </>
  ) : null;
}
